// hooks/useApi.js
// Generic API fetch hook
//
// שימוש:
//   const { data, loading, error, refetch } = useApi('/dashboard/summary');
//   const { data, loading, error, refetch } = useApi('/metrics', { params: { unit_id: 1 } });
//   const { data, loading, error } = useApi(unitId ? `/dashboard/category-breakdown?unit_id=${unitId}` : null);
//
// ★ כש-endpoint הוא null — לא שולח בקשה (conditional fetching)
// ★ data מוחזר כ-response.data.data (unwrapped מה-wrapper הסטנדרטי)
// ★ חריגים שמחזירים מערך ישיר ב-data (unit-scores, backup/list) — מטופלים אוטומטית

import { useState, useEffect, useCallback, useRef } from 'react';
import api, { extractErrorMessage } from '../services/api';

// =============================================================================
// useApi — GET request hook
// =============================================================================
export function useApi(endpoint, options = {}) {
  const {
    params = null,           // query params object
    autoFetch = true,        // האם לשלוח בקשה אוטומטית ב-mount
    defaultData = null,      // ערך ברירת מחדל ל-data
    transformResponse = null, // פונקציה לעיבוד data לפני שמירה
  } = options;

  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(!!endpoint && autoFetch);
  const [error, setError] = useState(null);

  // Abort controller לביטול בקשות ב-unmount או כש-endpoint משתנה
  const abortControllerRef = useRef(null);
  // Track current endpoint לזיהוי race conditions
  const currentEndpointRef = useRef(endpoint);

  // Serialized params — לזיהוי שינויים ב-params בלי reference comparison
  const paramsKey = params ? JSON.stringify(params) : '';
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // Transform ref — נשמר ב-ref כי functions לא יציבות בין renders
  const transformRef = useRef(transformResponse);
  transformRef.current = transformResponse;

  // ===================================================================
  // fetch — שולח GET request
  // ===================================================================
  const fetchData = useCallback(async (overrideEndpoint = null) => {
    const targetEndpoint = overrideEndpoint || endpoint;
    if (!targetEndpoint) {
      setLoading(false);
      return;
    }

    // ביטול בקשה קודמת
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const config = { signal: controller.signal };
      if (paramsRef.current) config.params = paramsRef.current;

      const response = await api.get(targetEndpoint, config);

      // בדיקה שהבקשה לא בוטלה
      if (controller.signal.aborted) return;

      // חילוץ data מה-response wrapper
      // מבנה סטנדרטי: { success: true, data: { ... } }
      // חריגים (מערך ישיר): { success: true, data: [...] }
      let responseData;
      if (response.data?.success !== undefined) {
        // Response wrapper סטנדרטי
        responseData = response.data.data;
      } else {
        // Response ללא wrapper (לא צפוי, אבל safe fallback)
        responseData = response.data;
      }

      // Transform אם הוגדר
      if (transformRef.current) {
        responseData = transformRef.current(responseData);
      }

      setData(responseData);
      setError(null);
    } catch (err) {
      // בדיקה שהשגיאה לא בגלל abort
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (controller.signal.aborted) return;

      setError(extractErrorMessage(err));
      // לא מנקים data — שומרים data ישן כ-stale data
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [endpoint]); // ★ רק endpoint — params ו-transform דרך refs

  // ===================================================================
  // Auto-fetch בעליית הקומפוננטה ובשינוי endpoint או params
  // ===================================================================
  useEffect(() => {
    currentEndpointRef.current = endpoint;

    if (endpoint && autoFetch) {
      fetchData();
    } else if (!endpoint) {
      // endpoint הפך ל-null — אפס state
      setData(defaultData);
      setLoading(false);
      setError(null);
    }

    return () => {
      // ביטול בקשה ב-unmount או שינוי endpoint/params
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // paramsKey: serialized params — שינוי ב-params יגרום ל-refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey, autoFetch, fetchData]);

  // ===================================================================
  // refetch — רענון ידני
  // ===================================================================
  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  // ===================================================================
  // setData — עדכון ידני של data (optimistic updates)
  // ===================================================================
  const updateData = useCallback((newData) => {
    setData((prev) => {
      if (typeof newData === 'function') return newData(prev);
      return newData;
    });
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    updateData,
  };
}

// =============================================================================
// useApiMutation — POST/PUT/DELETE hook
// =============================================================================
// שימוש:
//   const { mutate, loading, error } = useApiMutation();
//   const result = await mutate('post', '/categories', { name: 'חדש', parent_id: 1 });
//   const result = await mutate('put', '/categories/5', { name: 'מעודכן' });
//   const result = await mutate('delete', '/categories/5');
export function useApiMutation() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(async (method, endpoint, body = null) => {
    setLoading(true);
    setError(null);

    try {
      let response;
      switch (method.toLowerCase()) {
        case 'post':
          response = await api.post(endpoint, body);
          break;
        case 'put':
          response = await api.put(endpoint, body);
          break;
        case 'delete':
          response = await api.delete(endpoint, body ? { data: body } : undefined);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      // חילוץ data
      const responseData = response.data?.data ?? response.data;
      const message = response.data?.message || null;

      return {
        success: true,
        data: responseData,
        message,
        status: response.status,
      };
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
        status: err.response?.status,
        validationErrors: err.response?.status === 422
          ? (err.response?.data?.data?.details?.validation_errors || [])
          : [],
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // איפוס שגיאה
  const clearError = useCallback(() => setError(null), []);

  return {
    mutate,
    loading,
    error,
    clearError,
  };
}

export default useApi;
